import { z } from 'zod';
import { posClient } from './client';

export const STORE_SETUP_STEPS = [
  'visit_store',
  'review_products',
  'add_store_details',
  'set_up_payments',
  'set_up_delivery',
] as const;

export const storeSetupStepSchema = z.enum(STORE_SETUP_STEPS);
export type StoreSetupStep = z.infer<typeof storeSetupStepSchema>;

const storeSetupSummarySchema = z.object({
  store: z.object({
    name: z.string(),
    domain: z.string(),
    url: z.url(),
  }),
  trial: z.object({
    active: z.boolean(),
    ends_at: z.string().nullable(),
    days_remaining: z.number().int().nonnegative().nullable(),
  }),
  setup: z.object({
    completed_steps: z.array(storeSetupStepSchema),
    completed_count: z.number().int().nonnegative(),
    total_count: z.number().int().positive(),
    is_complete: z.boolean(),
    show_on_home: z.boolean(),
  }),
  readiness: z.object({
    products: z.object({
      total: z.number().int().nonnegative(),
      live: z.number().int().nonnegative(),
      starter: z.number().int().nonnegative(),
      custom: z.number().int().nonnegative(),
    }),
    payment_methods: z.object({ active: z.number().int().nonnegative() }),
    delivery: z.object({
      active_zones: z.number().int().nonnegative(),
      enabled_methods: z.number().int().nonnegative(),
    }),
    profile: z.object({
      has_applied_theme: z.boolean(),
      has_logo: z.boolean(),
      has_contact: z.boolean(),
    }),
  }),
});

const storeSetupEnvelopeSchema = z.object({
  success: z.literal(true),
  data: storeSetupSummarySchema,
});

export type StoreSetupSummary = z.infer<typeof storeSetupSummarySchema>;

function parseSummary(value: unknown): StoreSetupSummary {
  return storeSetupEnvelopeSchema.parse(value).data;
}

export const storeSetupApi = {
  async get(signal?: AbortSignal): Promise<StoreSetupSummary> {
    const response = await posClient.get('/store-setup', { signal });
    return parseSummary(response.data);
  },

  async setStep(step: StoreSetupStep, completed: boolean): Promise<StoreSetupSummary> {
    const response = await posClient.put(`/store-setup/steps/${step}`, { completed });
    return parseSummary(response.data);
  },

  async setHomeGuideVisible(visible: boolean): Promise<StoreSetupSummary> {
    const response = await posClient.put('/store-setup/home-guide', { visible });
    return parseSummary(response.data);
  },
};
